// src/admin/CreateLandingPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Alert, Spinner, Form, Button, Card, Tabs, Tab } from 'react-bootstrap';

// Şablonları dışarıdan içeri aktarıyoruz
import { getTemplateModules } from './LandingPageTemplates';

const DEFAULT_IMAGE = 'https://studio.littlehibba.com/images/sajeda/6.jpg';

const CreateLandingPage = () => {
    const navigate = useNavigate();
    const { id } = useParams(); 
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditing);
    const [message, setMessage] = useState({ text: '', type: '' });
    
    const [uploadingId, setUploadingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);

    const [pageData, setPageData] = useState({
        title: '',
        slug: '',
        seoDescription: '',
        seoKeywords: ''
    });

    const [blocks, setBlocks] = useState([]);

    useEffect(() => {
        const fetchPageData = async () => {
            try {
                const docRef = doc(db, 'customPages', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setPageData({
                        title: data.title || '',
                        slug: data.slug || '',
                        seoDescription: data.seoDescription || '',
                        seoKeywords: data.seoKeywords || ''
                    });
                    setBlocks(data.contentModules || []);
                } else {
                    setMessage({ text: 'Page not found.', type: 'danger' });
                }
            } catch (error) {
                setMessage({ text: 'Error fetching page data.', type: 'danger' });
            } finally {
                setFetching(false);
            }
        };

        if (isEditing) fetchPageData();
    }, [id, isEditing]);

    const generateSlug = (text) => {
        return text.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/-+/g, '-').replace(/^-+/, '').replace(/-+$/, '');            
    };

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setPageData({
            ...pageData,
            title: newTitle,
            slug: generateSlug(newTitle)
        });
    };

    // --- TEMPLATE MANAGEMENT ---
    const applyTemplate = (templateType) => {
        if (blocks.length > 0) {
            if (!window.confirm('Applying a template will clear your current canvas. Do you want to proceed?')) {
                return;
            }
        }
        
        const templateModules = getTemplateModules(templateType);
        
        const formattedModules = templateModules.map(mod => ({
            ...mod,
            style: mod.style || { align: 'center' }
        }));
        
        setBlocks(formattedModules);
    };

    // --- BLOCK MANAGEMENT ---
    const addBlock = (type) => {
        const newId = Date.now();
        let newBlock = { id: newId, type, style: { align: 'center' } };
        
        if (type === 'heading') {
            newBlock.content = 'Enter your heading here';
            newBlock.style = { ...newBlock.style, color: '#1a365d', fontSize: '36' };
        }
        if (type === 'text') {
            newBlock.content = 'This is a text block. You can change its color, size, and alignment from the style tab.';
            newBlock.style = { ...newBlock.style, color: '#4a5568', fontSize: '18' };
        }
        if (type === 'image') {
            newBlock.content = DEFAULT_IMAGE;
            newBlock.style = { ...newBlock.style, width: '100', borderRadius: '16' };
        }
        if (type === 'button') {
            newBlock.content = 'Click Here';
            newBlock.url = '#';
            newBlock.style = { ...newBlock.style, bgColor: '#0d6efd', textColor: '#ffffff', fontSize: '18', borderRadius: '50' };
        }
        if (type === 'html') {
            newBlock.content = '<div style="padding: 20px; background: #eee;">Custom HTML here</div>';
        }
        if (type === 'columns') {
            newBlock.colCount = 2;
            newBlock.columns = [
                { title: 'Column 1', text: 'Detail text for col 1', image: DEFAULT_IMAGE },
                { title: 'Column 2', text: 'Detail text for col 2', image: DEFAULT_IMAGE }
            ];
            newBlock.style = { bgColor: 'transparent', textColor: '#333333' };
        }

        setBlocks([...blocks, newBlock]);
        setEditingId(newId); 
    };

    const updateBlock = (id, updates) => {
        setBlocks(blocks.map(b => b.id === id ? { ...b, ...updates } : b));
    };

    const updateStyle = (id, styleUpdates) => {
        setBlocks(blocks.map(b => b.id === id ? { ...b, style: { ...b.style, ...styleUpdates } } : b));
    };

    const removeBlock = (id) => {
        if(window.confirm('Remove this element from canvas?')) {
            setBlocks(blocks.filter(b => b.id !== id));
            if(editingId === id) setEditingId(null);
        }
    };

    const moveBlock = (index, direction) => {
        const newBlocks = [...blocks];
        if (direction === 'up' && index > 0) {
            [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
        } else if (direction === 'down' && index < newBlocks.length - 1) {
            [newBlocks[index + 1], newBlocks[index]] = [newBlocks[index], newBlocks[index + 1]];
        }
        setBlocks(newBlocks);
    };

    const handleImageUpload = async (file, blockId, callback) => {
        if (!file) return;
        setUploadingId(blockId);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await fetch('https://studio.littlehibba.com/upload.php', { method: 'POST', body: uploadData });
            const data = await response.json();
            if (data.success) {
                callback(data.url);
            } else {
                alert('Upload failed: ' + data.message);
            }
        } catch (error) {
            alert('Error uploading file: ' + error.message);
        } finally {
            setUploadingId(null);
        }
    };

    const handleSavePage = async (e) => {
        e.preventDefault();
        if (!pageData.title || !pageData.slug) {
            setMessage({ text: 'Title and Slug required.', type: 'danger' });
            return;
        }
        
        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            const pagePayload = { ...pageData, contentModules: blocks, updatedAt: serverTimestamp(), status: 'published' };
            if (isEditing) {
                await updateDoc(doc(db, 'customPages', id), pagePayload);
                setMessage({ text: 'Canvas updated successfully!', type: 'success' });
            } else {
                pagePayload.createdAt = serverTimestamp();
                await addDoc(collection(db, 'customPages'), pagePayload);
                setMessage({ text: 'Canvas created successfully!', type: 'success' });
            }
            setTimeout(() => navigate('/admin/manage-landing-pages'), 2000);
        } catch (error) {
            setMessage({ text: 'An error occurred: ' + error.message, type: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    // --- RENDER VIEW (CANVAS) ---
    const renderViewBlock = (block) => {
        const s = block.style || {};
        switch(block.type) {
            case 'heading':
                return <h2 className={`text-${s.align} fw-bolder my-3`} style={{ color: s.color, fontSize: `${s.fontSize}px`, fontFamily: "'Playfair Display', serif" }}>{block.content}</h2>;
            case 'text':
                return <p className={`text-${s.align} my-3`} style={{ color: s.color, fontSize: `${s.fontSize}px`, whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{block.content}</p>;
            case 'image':
                return (
                    <div className={`text-${s.align} my-4`}>
                        <img src={block.content || DEFAULT_IMAGE} alt="Canvas element" className="img-fluid shadow-sm" style={{ width: `${s.width}%`, borderRadius: `${s.borderRadius}px`, objectFit: 'cover' }} />
                    </div>
                );
            case 'button':
                return (
                    <div className={`text-${s.align} my-4`}>
                        <a href={block.url || '#'} className="btn shadow-sm fw-bold px-5 py-2" style={{ backgroundColor: s.bgColor, color: s.textColor, fontSize: `${s.fontSize}px`, borderRadius: `${s.borderRadius}px`, border: 'none' }}>{block.content}</a>
                    </div>
                );
            case 'html':
                return <div dangerouslySetInnerHTML={{ __html: block.content }} />;
            case 'columns':
                return (
                    <div className="row g-4 my-4" style={{ backgroundColor: s.bgColor }}>
                        {block.columns.map((col, i) => (
                            <div key={i} className={`col-md-${12 / block.colCount} text-center`}>
                                {col.image && <img src={col.image} alt="Col" className="img-fluid mb-3 rounded-4 shadow-sm" style={{ maxHeight: '250px', objectFit: 'cover' }} />}
                                <h4 style={{ color: s.textColor, fontFamily: "'Playfair Display', serif" }}>{col.title}</h4>
                                <p style={{ color: s.textColor, opacity: 0.8 }}>{col.text}</p>
                            </div>
                        ))}
                    </div>
                );
            default: return null;
        }
    };

    // --- RENDER EDIT (INLINE SIDEBAR) ---
    const renderEditBlock = (block) => {
        const s = block.style || {};
        return (
            <div className="bg-white p-4 rounded-4 border shadow-lg position-relative mt-2" style={{ zIndex: 100 }}>
                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <h6 className="fw-bold text-primary m-0 text-uppercase"><i className="bi bi-sliders me-2"></i>Edit {block.type}</h6>
                    <Button variant="light" size="sm" onClick={() => setEditingId(null)}><i className="bi bi-x-lg"></i> Close</Button>
                </div>

                <Tabs defaultActiveKey="content" className="mb-3">
                    <Tab eventKey="content" title="Content">
                        {(block.type === 'heading' || block.type === 'text') && (
                            <Form.Control as={block.type === 'text' ? "textarea" : "input"} rows={5} value={block.content} onChange={(e) => updateBlock(block.id, { content: e.target.value })} />
                        )}

                        {block.type === 'image' && (
                            <>
                                <Form.Label className="small fw-bold text-muted mt-2">Image URL</Form.Label>
                                <Form.Control type="url" value={block.content} onChange={(e) => updateBlock(block.id, { content: e.target.value })} />
                                <Form.Label className="small fw-bold text-muted mt-3">Upload from PC</Form.Label>
                                <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], block.id, (url) => updateBlock(block.id, { content: url }))} />
                                {uploadingId === block.id && <small className="text-primary mt-1">Uploading...</small>}
                            </>
                        )}

                        {block.type === 'button' && (
                            <div className="row g-2">
                                <div className="col-6"><Form.Label className="small">Label</Form.Label><Form.Control type="text" value={block.content} onChange={(e) => updateBlock(block.id, { content: e.target.value })} /></div>
                                <div className="col-6"><Form.Label className="small">Target URL</Form.Label><Form.Control type="text" value={block.url} onChange={(e) => updateBlock(block.id, { url: e.target.value })} /></div>
                            </div>
                        )}

                        {block.type === 'html' && (
                            <>
                                <Form.Control as="textarea" rows={6} className="font-monospace bg-dark text-light" value={block.content} onChange={(e) => updateBlock(block.id, { content: e.target.value })} />
                                <small className="text-danger d-block mt-1">Careful: Invalid HTML can break the page layout.</small>
                            </>
                        )}

                        {block.type === 'columns' && (
                            <>
                                <Form.Label className="small fw-bold">Number of Columns</Form.Label>
                                <Form.Select value={block.colCount} onChange={(e) => {
                                    const count = parseInt(e.target.value);
                                    let newCols = [...block.columns];
                                    while(newCols.length < count) newCols.push({ title: 'New Col', text: 'Text', image: '' });
                                    newCols = newCols.slice(0, count);
                                    updateBlock(block.id, { colCount: count, columns: newCols });
                                }} className="mb-3">
                                    <option value="1">1 Column</option><option value="2">2 Columns</option><option value="3">3 Columns</option><option value="4">4 Columns</option>
                                </Form.Select>
                                
                                {block.columns.map((col, i) => (
                                    <Card key={i} className="mb-2 bg-light border-0 p-2">
                                        <div className="fw-bold small mb-2 text-primary">Column {i+1}</div>
                                        <Form.Control type="text" size="sm" value={col.title} placeholder="Title" className="mb-1" onChange={(e) => {
                                            const newCols = [...block.columns]; newCols[i].title = e.target.value; updateBlock(block.id, { columns: newCols });
                                        }} />
                                        <Form.Control as="textarea" size="sm" rows={2} value={col.text} placeholder="Text" className="mb-1" onChange={(e) => {
                                            const newCols = [...block.columns]; newCols[i].text = e.target.value; updateBlock(block.id, { columns: newCols });
                                        }} />
                                        <Form.Control type="url" size="sm" value={col.image} placeholder="Image URL" onChange={(e) => {
                                            const newCols = [...block.columns]; newCols[i].image = e.target.value; updateBlock(block.id, { columns: newCols });
                                        }} />
                                    </Card>
                                ))}
                            </>
                        )}
                    </Tab>

                    <Tab eventKey="style" title="Style">
                        {s.align && (
                            <div className="mb-3">
                                <label className="small fw-bold d-block mb-1">Alignment</label>
                                <div className="btn-group btn-group-sm w-100">
                                    <Button variant={s.align === 'start' ? 'dark' : 'outline-secondary'} onClick={() => updateStyle(block.id, { align: 'start' })}><i className="bi bi-text-left"></i> Left</Button>
                                    <Button variant={s.align === 'center' ? 'dark' : 'outline-secondary'} onClick={() => updateStyle(block.id, { align: 'center' })}><i className="bi bi-text-center"></i> Center</Button>
                                    <Button variant={s.align === 'end' ? 'dark' : 'outline-secondary'} onClick={() => updateStyle(block.id, { align: 'end' })}><i className="bi bi-text-right"></i> Right</Button>
                                </div>
                            </div>
                        )}

                        {(block.type === 'heading' || block.type === 'text') && (
                            <div className="row g-2">
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Color</Form.Label>
                                    <Form.Control type="color" value={s.color} onChange={(e) => updateStyle(block.id, { color: e.target.value })} title="Choose your color" className="w-100 p-1" />
                                </div>
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Size ({s.fontSize}px)</Form.Label>
                                    <Form.Range min="12" max="72" value={s.fontSize} onChange={(e) => updateStyle(block.id, { fontSize: e.target.value })} />
                                </div>
                            </div>
                        )}

                        {block.type === 'image' && (
                            <div className="row g-2">
                                <div className="col-12">
                                    <Form.Label className="small fw-bold">Width ({s.width}%)</Form.Label>
                                    <Form.Range min="10" max="100" value={s.width} onChange={(e) => updateStyle(block.id, { width: e.target.value })} />
                                </div>
                                <div className="col-12">
                                    <Form.Label className="small fw-bold">Border Radius ({s.borderRadius}px)</Form.Label>
                                    <Form.Range min="0" max="100" value={s.borderRadius} onChange={(e) => updateStyle(block.id, { borderRadius: e.target.value })} />
                                </div>
                            </div>
                        )}

                        {block.type === 'button' && (
                            <div className="row g-2">
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Background</Form.Label>
                                    <Form.Control type="color" value={s.bgColor} onChange={(e) => updateStyle(block.id, { bgColor: e.target.value })} className="w-100 p-1" />
                                </div>
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Text Color</Form.Label>
                                    <Form.Control type="color" value={s.textColor} onChange={(e) => updateStyle(block.id, { textColor: e.target.value })} className="w-100 p-1" />
                                </div>
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Font Size ({s.fontSize}px)</Form.Label>
                                    <Form.Range min="12" max="36" value={s.fontSize} onChange={(e) => updateStyle(block.id, { fontSize: e.target.value })} />
                                </div>
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Roundness ({s.borderRadius}px)</Form.Label>
                                    <Form.Range min="0" max="50" value={s.borderRadius} onChange={(e) => updateStyle(block.id, { borderRadius: e.target.value })} />
                                </div>
                            </div>
                        )}

                        {block.type === 'columns' && (
                            <div className="row g-2">
                                <div className="col-6">
                                    <Form.Label className="small fw-bold">Text Color</Form.Label>
                                    <Form.Control type="color" value={s.textColor} onChange={(e) => updateStyle(block.id, { textColor: e.target.value })} className="w-100 p-1" />
                                </div>
                            </div>
                        )}
                    </Tab>
                </Tabs>
            </div>
        );
    }

    if (fetching) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

    return (
        <div className="container-fluid p-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div>
                    <h1 className="fw-bold h3 text-dark mb-1">{isEditing ? 'Edit Canvas Page' : 'Little Hibba Page Builder'}</h1>
                    <p className="text-muted mb-0">Build your page visually with premium components and style controls.</p>
                </div>
                <div className="d-flex gap-2">
                    <Link to="/admin/manage-landing-pages" className="btn btn-outline-secondary px-3 rounded-pill">Back</Link>
                    <Button variant="primary" onClick={handleSavePage} disabled={loading} className="px-4 fw-bold rounded-pill shadow-sm">
                        {loading ? 'Saving...' : 'Save Page'}
                    </Button>
                </div>
            </div>

            {/* EKLENEN MESAJ ALANI BURASI */}
            {message.text && (
                <Alert variant={message.type} className="shadow-sm border-0 mb-4 rounded-3">
                    {message.text}
                </Alert>
            )}

            <div className="row g-4">
                {/* LEFT PANEL: Elements & Settings */}
                <div className="col-lg-3">
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 px-3"><h6 className="fw-bold text-dark">Basic Info</h6></Card.Header>
                        <Card.Body className="p-3">
                            <Form.Control type="text" size="sm" value={pageData.title} placeholder="Page Title" onChange={handleTitleChange} className="mb-2" required />
                            <Form.Control type="text" size="sm" value={pageData.slug} placeholder="URL Slug" onChange={(e) => setPageData({...pageData, slug: e.target.value})} className="mb-2" required />
                            <Form.Control type="text" size="sm" value={pageData.seoKeywords} placeholder="Keywords" onChange={(e) => setPageData({...pageData, seoKeywords: e.target.value})} className="mb-2" />
                            <Form.Control as="textarea" rows={2} size="sm" value={pageData.seoDescription} placeholder="Meta Desc" onChange={(e) => setPageData({...pageData, seoDescription: e.target.value})} />
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 px-3"><h6 className="fw-bold text-success">Templates</h6></Card.Header>
                        <Card.Body className="p-3">
                            <div className="d-grid gap-2">
                                <Button variant="outline-success" size="sm" className="text-start" onClick={() => applyTemplate('premium_author')}>
                                    <i className="bi bi-person-badge me-2"></i> Author Profile
                                </Button>
                                <Button variant="outline-success" size="sm" className="text-start" onClick={() => applyTemplate('premium_book')}>
                                    <i className="bi bi-book me-2"></i> Book Launch
                                </Button>
                                <Button variant="outline-success" size="sm" className="text-start" onClick={() => applyTemplate('event_page')}>
                                    <i className="bi bi-calendar-event me-2"></i> Event Registration
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 position-sticky" style={{ top: '20px' }}>
                        <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 px-3"><h6 className="fw-bold text-primary">Elements Palette</h6></Card.Header>
                        <Card.Body className="p-3">
                            <div className="d-grid gap-2">
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('heading')}><i className="bi bi-type-h1 me-2 text-primary"></i> Heading</Button>
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('text')}><i className="bi bi-text-paragraph me-2 text-primary"></i> Text</Button>
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('image')}><i className="bi bi-image me-2 text-success"></i> Image</Button>
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('button')}><i className="bi bi-hand-index-thumb me-2 text-warning"></i> Button</Button>
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('columns')}><i className="bi bi-layout-split me-2 text-info"></i> Columns / Grid</Button>
                                <Button variant="outline-dark" size="sm" className="text-start" onClick={() => addBlock('html')}><i className="bi bi-code-slash me-2 text-danger"></i> Custom HTML</Button>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* RIGHT PANEL: Canvas */}
                <div className="col-lg-9">
                    <div className="bg-white shadow-sm rounded-4 border p-4 p-md-5" style={{ minHeight: '800px' }}>
                        {blocks.length === 0 ? (
                            <div className="text-center mt-5 pt-5 text-muted">
                                <i className="bi bi-magic fs-1 mb-3 d-block"></i>
                                <h4>Your canvas is empty</h4>
                                <p>Click an element on the left to start building.</p>
                            </div>
                        ) : (
                            blocks.map((block, idx) => (
                                <div 
                                    key={block.id} 
                                    className="position-relative mb-2 transition-all"
                                    onMouseEnter={() => setHoveredId(block.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    style={{ border: hoveredId === block.id && editingId !== block.id ? '2px dashed #0d6efd' : '2px dashed transparent', padding: '10px', borderRadius: '12px' }}
                                >
                                    {editingId === block.id ? (
                                        renderEditBlock(block)
                                    ) : (
                                        <>
                                            <div onClick={() => setEditingId(block.id)} style={{ cursor: 'pointer' }}>
                                                {renderViewBlock(block)}
                                            </div>
                                            {hoveredId === block.id && (
                                                <div className="position-absolute top-0 end-0 bg-white shadow border rounded-pill px-2 py-1 m-2" style={{ zIndex: 10 }}>
                                                    <Button variant="link" size="sm" className="text-primary p-0 mx-2" onClick={() => setEditingId(block.id)}><i className="bi bi-pencil-fill"></i></Button>
                                                    <Button variant="link" size="sm" className="text-secondary p-0 mx-2" onClick={() => moveBlock(idx, 'up')} disabled={idx === 0}><i className="bi bi-arrow-up-circle-fill"></i></Button>
                                                    <Button variant="link" size="sm" className="text-secondary p-0 mx-2" onClick={() => moveBlock(idx, 'down')} disabled={idx === blocks.length - 1}><i className="bi bi-arrow-down-circle-fill"></i></Button>
                                                    <Button variant="link" size="sm" className="text-danger p-0 mx-2" onClick={() => removeBlock(block.id)}><i className="bi bi-trash-fill"></i></Button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateLandingPage;